"use client";

import type {
  DispatchBoard,
  DispatchFacility,
  DispatchJob,
  DispatchNurse,
} from "@vellum/vellum-api";
import { type ReactNode, useMemo } from "react";

import { Badge } from "../components/Badge";
import { cn } from "../lib/utils";
import {
  type BoardIndex,
  JOB_TYPE_LABELS,
  formatClock,
  formatDuration,
  groupJobs,
  secondsBetween,
} from "./board";

type Detail = { text: string; warn: boolean };

/** The one line under a job that says what a dispatcher needs to know about it now. */
function jobDetail(
  job: DispatchJob,
  timeZone: string,
  generatedAt: string,
): Detail {
  const clock = (iso: string) => formatClock(iso, timeZone);
  if (job.check_in_at && job.check_out_at) {
    return {
      text: `Visited ${clock(job.check_in_at)}–${clock(job.check_out_at)}`,
      warn: false,
    };
  }
  if (job.check_in_at) {
    return { text: `On site since ${clock(job.check_in_at)}`, warn: false };
  }
  if (job.late && job.due_at) {
    return {
      text: `${formatDuration(secondsBetween(generatedAt, job.due_at))} past due, not checked in`,
      warn: true,
    };
  }
  if (job.at_risk && job.earliest_arrival) {
    return {
      text: `Can't arrive before ${clock(job.earliest_arrival)}`,
      warn: true,
    };
  }
  if (job.nurse_id && !job.due_at) {
    return { text: "Waiting on the nurse's ETA", warn: true };
  }
  if (job.earliest_arrival) {
    return {
      text: `Next stop, can arrive by ${clock(job.earliest_arrival)}`,
      warn: false,
    };
  }
  if (job.nurse_eta) {
    return { text: `Nurse ETA ${clock(job.nurse_eta)}`, warn: false };
  }
  return { text: job.due_at ? "Scheduled" : "Requested ASAP", warn: false };
}

function JobBadge({ job }: { job: DispatchJob }) {
  if (job.late) return <Badge tone="strong">Late</Badge>;
  if (job.at_risk) return <Badge tone="warning">At risk</Badge>;
  if (job.check_in_at && !job.check_out_at)
    return <Badge tone="neutral">On site</Badge>;
  if (job.carried_over) return <Badge tone="muted">Carried over</Badge>;
  return null;
}

function JobRow({
  job,
  facility,
  nurse,
  generatedAt,
  href,
  selected,
  onSelect,
}: {
  job: DispatchJob;
  facility: DispatchFacility | undefined;
  nurse: DispatchNurse | undefined;
  generatedAt: string;
  href: string;
  selected: boolean;
  onSelect?: () => void;
}) {
  const timeZone = facility?.timezone ?? "UTC";
  const detail = jobDetail(job, timeZone, generatedAt);
  const type = JOB_TYPE_LABELS[job.job_type] ?? job.job_type;
  const due = job.due_at
    ? formatClock(job.due_at, timeZone, job.carried_over)
    : "ASAP";
  const facilityName = facility?.name ?? "Unknown facility";
  const content = (
    <>
      <span className="font-medium tabular-nums text-gray-900">{due}</span>
      <span className="min-w-0">
        <span className="block truncate font-medium text-gray-900">
          {facilityName}
          {nurse ? (
            <span className="font-normal text-gray-600"> · {nurse.name}</span>
          ) : null}
        </span>
        <span
          className={cn(
            "block truncate text-xs",
            detail.warn ? "font-medium text-amber-800" : "text-gray-600",
          )}
        >
          {detail.warn && (
            <span
              aria-hidden
              className="iconoir-warning-triangle mr-1 align-[-2px]"
            />
          )}
          {type} · {detail.text}
        </span>
      </span>
      <JobBadge job={job} />
    </>
  );
  const gridClass =
    "grid min-w-0 flex-1 grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-2 py-2 pl-3 text-left";
  // The link sits beside the row's button rather than inside it, as a link
  // can't be nested in a button.
  return (
    <div
      className={cn(
        "flex border-b border-gray-100 text-sm last:border-b-0",
        selected && "bg-gray-100 shadow-[inset_3px_0_0_#1c1917]",
        onSelect && !selected && "hover:bg-gray-50",
      )}
    >
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className={gridClass}
        >
          {content}
        </button>
      ) : (
        <div className={gridClass}>{content}</div>
      )}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        title="Open in the app"
        aria-label={`Open the ${due} job at ${facilityName} in the app`}
        className="flex items-center pr-3 pl-2 text-gray-500 hover:text-gray-900"
      >
        <span aria-hidden className="iconoir-open-new-window" />
      </a>
    </div>
  );
}

function Section({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count: number;
  empty: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <h2 className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900">
        {title}
        <span className="text-xs font-medium text-gray-500">{count}</span>
      </h2>
      {count === 0 ? (
        <p className="px-3 py-3 text-sm text-gray-500">{empty}</p>
      ) : (
        children
      )}
    </section>
  );
}

function NurseSummary({
  nurse,
  index,
  onClear,
}: {
  nurse: DispatchNurse;
  index: BoardIndex;
  onClear: () => void;
}) {
  const at = nurse.facility_id
    ? index.facilities.get(nurse.facility_id)
    : undefined;
  const clock = (iso: string) => formatClock(iso, at?.timezone ?? "UTC");
  const state = {
    on_site:
      at && nurse.since
        ? `On site at ${at.name} since ${clock(nurse.since)}`
        : "On site",
    between_stops:
      at && nurse.since
        ? `Left ${at.name} at ${clock(nurse.since)}`
        : "Between stops",
    not_started: "Hasn't checked in anywhere yet today",
    done:
      at && nurse.since
        ? `Finished at ${at.name} at ${clock(nurse.since)}`
        : "Finished for the day",
  }[nurse.state];

  // The leg names its facility, so this works for a stop in another region.
  const nextLeg = nurse.legs.find((leg) => leg.state === "next");
  const nextFacility = nextLeg
    ? index.facilities.get(nextLeg.to_facility_id)
    : undefined;

  return (
    <section
      aria-label={`${nurse.name}'s day`}
      className="rounded-lg border border-gray-900 bg-white p-3 text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900">{nurse.name}</h2>
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-medium text-gray-600 underline hover:text-gray-900"
        >
          Show everyone
        </button>
      </div>
      <p className="text-gray-700">{state}</p>
      {nextFacility && (
        <p className="text-gray-700">
          Next: {nextFacility.name}
          {nextLeg?.drive_seconds != null &&
            `, ${formatDuration(nextLeg.drive_seconds)} drive`}
        </p>
      )}
      <p className="text-xs text-gray-500">
        {nurse.job_ids.length} {nurse.job_ids.length === 1 ? "stop" : "stops"}{" "}
        today
      </p>
    </section>
  );
}

type Props = {
  board: DispatchBoard;
  index: BoardIndex;
  selectedNurseId: string | null;
  onSelectNurse: (nurseId: string | null) => void;
  jobHref: (job: DispatchJob) => string;
};

/** The side panel: who needs a nurse, then everyone's stops in the order they're due. */
export function DispatchQueue({
  board,
  index,
  selectedNurseId,
  onSelectNurse,
  jobHref,
}: Props) {
  const groups = useMemo(() => groupJobs(board.jobs), [board.jobs]);
  const late = board.jobs.filter((job) => job.late).length;
  const atRisk = board.jobs.filter((job) => job.at_risk).length;
  const selected = selectedNurseId
    ? index.nurses.get(selectedNurseId)
    : undefined;

  const row = (job: DispatchJob) => (
    <JobRow
      key={job.id}
      job={job}
      facility={index.facilities.get(job.facility_id)}
      nurse={job.nurse_id ? index.nurses.get(job.nurse_id) : undefined}
      generatedAt={board.generated_at}
      href={jobHref(job)}
      selected={job.nurse_id !== null && job.nurse_id === selectedNurseId}
      onSelect={
        job.nurse_id
          ? () =>
              onSelectNurse(
                job.nurse_id === selectedNurseId ? null : job.nurse_id!,
              )
          : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-700">
        <span className="font-semibold text-gray-900">{board.jobs.length}</span>{" "}
        jobs ·{" "}
        <span className="font-semibold text-gray-900">
          {groups.unassigned.length}
        </span>{" "}
        unassigned · <span className="font-semibold text-gray-900">{late}</span>{" "}
        late · <span className="font-semibold text-gray-900">{atRisk}</span> at
        risk
      </p>

      {selected && (
        <NurseSummary
          nurse={selected}
          index={index}
          onClear={() => onSelectNurse(null)}
        />
      )}

      <Section
        title="Unassigned"
        count={groups.unassigned.length}
        empty="Every job today has a nurse."
      >
        {groups.unassigned.map(row)}
      </Section>

      <Section
        title="Today by ETA"
        count={groups.upcoming.length}
        empty="No assigned jobs left to visit today."
      >
        {groups.upcoming.map(row)}
      </Section>

      {groups.done.length > 0 && (
        <details className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <summary className="cursor-pointer bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900">
            Visited ({groups.done.length})
          </summary>
          {groups.done.map(row)}
        </details>
      )}
    </div>
  );
}
